import * as React from "react"
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu"
import { Ellipsis, X } from "lucide-react"

import { cn } from "@/lib/utils"
import { formatNumber } from "@/lib/format"
import { useConfirm } from "@/components/ui/confirm-dialog"
import { useToastClearance } from "@/components/ui/unsaved-bar"
import { useIsPhone } from "@/hooks/useMediaQuery"

/*
  BulkBar — the floating navy bar for a selection: "(3) selected · Set category · Export · ⋯ · × Clear".
  It sticks to the bottom of the window (above the phone tab bar) while the list scrolls, rests under
  the list at its end, and lifts the toasts above itself. Hidden when `count` is 0.

    <BulkBar count={selected.length} onClear={clear} testId="catalog-bulkbar"
      actions={[
        { label: 'Merge…', icon: GitMerge, onSelect: merge, disabled: selected.length < 2 },
        { label: 'Set category', icon: Tag, onSelect: openCategory },
        { label: 'Export', icon: Download, onSelect: exportRows },
      ]}
      moreActions={[
        { label: 'Copy part numbers', icon: Copy, onSelect: copy },
        { label: 'Delete items…', icon: Trash2, danger: true, onSelect: remove,
          confirm: { title: `Delete ${n} catalog items?`, requireText: 'DELETE' } },
      ]} />

  - Danger actions are ALWAYS behind ⋯ (a danger item in `actions` is moved there), last, in red, and
    ask first with the app ConfirmDialog (`confirm` = useConfirm options; a sensible default otherwise).
    Pass `confirm: false` only when onSelect opens its own confirmation dialog.
  - Keyboard: Tab reaches every button, ←/→ move between them, Escape clears the selection.
    Clearing removes the bar; when focus was in it, `returnFocus()` puts it back on the list (DataTable
    passes "the current row").
  - On phones only the first `phoneActions` (1) action stays visible, so the bar keeps to one line on a
    390px screen; the rest join the ⋯ menu.
  Action: { key, label, icon, onSelect, disabled, title, hint, danger, confirm, testId }.
  Props: count, noun ('selected'), label (replaces "N selected"), onClear, returnFocus, clearLabel,
  actions, moreActions, moreLabel, phoneActions, children (extra BulkButtons), className, testId.
  BulkButton / BulkDivider are exported for custom content.
*/

const BulkButton = React.forwardRef(function BulkButton({ icon: Icon, children, className, ...props }, ref) {
  return (
    <button
      ref={ref}
      type="button"
      className={cn(
        "inline-flex h-[30px] items-center gap-1.5 whitespace-nowrap rounded-lg bg-white/10 px-2.5 text-[12.5px] font-semibold text-white transition-colors hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 disabled:pointer-events-none disabled:opacity-45",
        className
      )}
      {...props}
    >
      {Icon ? <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" /> : null}
      {children}
    </button>
  )
})

const BulkDivider = () => <span className="mx-0.5 h-5 w-px shrink-0 bg-white/20" aria-hidden="true" />

const menuItemClasses =
  "relative flex h-[34px] cursor-default select-none items-center gap-2.5 rounded-lg px-2.5 text-[13px] text-ink outline-none transition-colors data-[highlighted]:bg-surface-hover data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&>svg]:h-[15px] [&>svg]:w-[15px] [&>svg]:shrink-0 [&>svg]:text-ink-3"

function actionKey(action, index) {
  return action.key || `${action.label}-${index}`
}

function BulkBar({
  count = 0,
  noun = "selected",
  label,
  onClear,
  returnFocus,
  clearLabel = "Clear",
  actions = [],
  moreActions = [],
  moreLabel = "More actions",
  phoneActions = 1,
  children,
  className,
  testId = "bulk-bar",
}) {
  const confirm = useConfirm()
  const isPhone = useIsPhone()
  const barRef = React.useRef(null)
  const visible = count > 0
  useToastClearance(barRef, visible)

  if (!visible) return null

  const cleanActions = actions.filter((action) => action && !action.hidden)
  const shown = cleanActions.filter((action) => !action.danger)
  const inline = isPhone ? shown.slice(0, phoneActions) : shown
  const overflow = isPhone ? shown.slice(phoneActions) : []
  const menuSafe = [...overflow, ...moreActions.filter((action) => action && !action.hidden && !action.danger)]
  const menuDanger = [
    ...cleanActions.filter((action) => action.danger),
    ...moreActions.filter((action) => action && !action.hidden && action.danger),
  ]
  const countText = formatNumber(count, { maxDecimals: 0 })

  const run = async (action) => {
    if (!action.onSelect || action.disabled) return
    if (action.danger && action.confirm !== false) {
      const base = String(action.label || "Continue").replace(/…$/, "")
      const ok = await confirm({
        title: `${base}?`,
        description: `This applies to the ${countText} selected ${count === 1 ? "item" : "items"}.`,
        tone: "danger",
        confirmLabel: base,
        ...(action.confirm && typeof action.confirm === "object" ? action.confirm : {}),
      })
      if (!ok) return
    }
    action.onSelect()
  }

  // The bar goes away with the selection: do not leave keyboard focus on a removed button (<body>).
  const clear = () => {
    const hadFocus = Boolean(barRef.current && barRef.current.contains(document.activeElement))
    onClear?.()
    if (hadFocus) returnFocus?.()
  }

  const onKeyDown = (event) => {
    if (event.key === "Escape" && onClear) {
      event.preventDefault()
      event.stopPropagation()
      clear()
      return
    }
    if (event.key !== "ArrowRight" && event.key !== "ArrowLeft") return
    const buttons = [...(barRef.current?.querySelectorAll("button:not([disabled])") || [])]
    const index = buttons.indexOf(document.activeElement)
    if (index < 0) return
    event.preventDefault()
    const next = buttons[(index + (event.key === "ArrowRight" ? 1 : -1) + buttons.length) % buttons.length]
    next?.focus()
  }

  const renderMenuItem = (action, index) => {
    const Icon = action.icon
    return (
      <DropdownMenuPrimitive.Item
        key={actionKey(action, index)}
        disabled={action.disabled}
        data-testid={action.testId}
        data-danger={action.danger ? "true" : undefined}
        className={cn(
          menuItemClasses,
          action.danger && "text-status-red-ink data-[highlighted]:bg-status-red-bg [&>svg]:text-status-red-ink"
        )}
        onSelect={() => setTimeout(() => run(action), 0)}
      >
        {Icon ? <Icon aria-hidden="true" /> : null}
        <span className="min-w-0 flex-1 truncate">{action.label}</span>
        {action.hint ? <span className="ml-3 shrink-0 text-[11.5px] font-medium text-ink-3">{action.hint}</span> : null}
      </DropdownMenuPrimitive.Item>
    )
  }

  return (
    <div
      className={cn(
        "pointer-events-none sticky bottom-[calc(1rem+var(--app-bottom-nav-height,0px))] z-20 mt-3 flex justify-center px-2",
        className
      )}
      data-testid={testId}
    >
      <div
        ref={barRef}
        role="toolbar"
        aria-label={`Bulk actions for ${countText} ${noun}`}
        onKeyDown={onKeyDown}
        className="pointer-events-auto flex max-w-full flex-wrap items-center gap-1.5 rounded-xl bg-[color:var(--navy)] py-2 pl-3 pr-2 text-[13px] font-semibold text-white shadow-overlay"
      >
        <span className="mr-1 inline-flex items-center gap-2 whitespace-nowrap" aria-live="polite">
          {label ?? (
            <>
              <span className="grid h-[22px] min-w-[22px] place-items-center rounded-full bg-white px-1.5 text-[11.5px] font-extrabold tabular-nums text-[color:var(--navy)]" data-testid="bulkbar-count">
                {countText}
              </span>
              <span>{noun}</span>
            </>
          )}
        </span>
        {inline.map((action, index) => (
          <BulkButton
            key={actionKey(action, index)}
            icon={action.icon}
            onClick={() => run(action)}
            disabled={action.disabled}
            title={action.title}
            data-testid={action.testId}
          >
            {action.label}
          </BulkButton>
        ))}
        {children}
        {menuSafe.length || menuDanger.length ? (
          <DropdownMenuPrimitive.Root modal={false}>
            <DropdownMenuPrimitive.Trigger asChild>
              <BulkButton aria-label={moreLabel} title={moreLabel} className="w-[30px] justify-center px-0" data-testid={`${testId}-more`}>
                <Ellipsis className="h-4 w-4" aria-hidden="true" />
              </BulkButton>
            </DropdownMenuPrimitive.Trigger>
            <DropdownMenuPrimitive.Portal>
              <DropdownMenuPrimitive.Content
                side="top"
                align="end"
                sideOffset={8}
                collisionPadding={8}
                // React events bubble through portals: keep the menu's Esc / arrows away from the bar.
                onKeyDown={(event) => event.stopPropagation()}
                className="z-50 min-w-[210px] max-w-[320px] overflow-hidden rounded-xl border border-line bg-surface p-1.5 text-ink shadow-overlay data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
              >
                {menuSafe.map(renderMenuItem)}
                {menuSafe.length && menuDanger.length ? <DropdownMenuPrimitive.Separator className="my-1 h-px bg-line-2" /> : null}
                {menuDanger.map((action, index) => renderMenuItem(action, menuSafe.length + index))}
              </DropdownMenuPrimitive.Content>
            </DropdownMenuPrimitive.Portal>
          </DropdownMenuPrimitive.Root>
        ) : null}
        {onClear ? (
          <>
            <BulkDivider />
            <BulkButton icon={X} onClick={clear} aria-label="Clear selection" title="Clear selection (Esc)" className="bg-transparent hover:bg-white/10" data-testid={`${testId}-clear`}>
              <span className="max-sm:hidden">{clearLabel}</span>
            </BulkButton>
          </>
        ) : null}
      </div>
    </div>
  )
}

export { BulkBar, BulkButton, BulkDivider }
